import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import type { DocumentEditorRef, DocumentEditorProps, EditorSelection } from '../composer/EditorContracts';

/**
 * LexicalDocumentEditor - Minimal stub implementation
 * 
 * This is a placeholder editor that will be implemented in Phase 2.
 * Currently provides no-op methods to satisfy the DocumentOrchestrator imports.
 */

const LexicalDocumentEditor = forwardRef<DocumentEditorRef, DocumentEditorProps>(function LexicalDocumentEditor(
  { docId, readOnly, services, zoomMode, userZoom, onDirtyChange, onSelectionChange, onStatus },
  ref
) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onStatus?.({ kind: 'info', msg: `LexicalDocumentEditor mounted for docId=${docId}` });
    onDirtyChange?.(false);
    onSelectionChange?.({
      pagesInvolved: 1,
      charCount: 0,
      wordCount: 0,
      start: { page: 0 as any, x: 0 as any, y: 0 as any },
      end:   { page: 0 as any, x: 0 as any, y: 0 as any },
    });
  }, [docId, onDirtyChange, onSelectionChange, onStatus]);

  useImperativeHandle(ref, () => ({
    getSelection(): EditorSelection {
      return {
        isCollapsed: true,
        anchor: { page: 0 as any, x: 0 as any, y: 0 as any },
        focus:  { page: 0 as any, x: 0 as any, y: 0 as any },
      };
    },
    setSelection() { /* no-op */ },
    async applyDelta() { /* no-op */ },
    undo() { /* no-op */ },
    redo() { /* no-op */ },
    scrollTo() { rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); },
    focus() { rootRef.current?.focus(); },
    blur() { rootRef.current?.blur(); },
    getCapabilities() { return { pagination: false, richText: true, realtimeCollab: true, exportPdf: false, printPreview: false }; },
    getContentHash() { return 'lexical-stub'; },
    dispose() { /* no-op */ },
  }), []);

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      className="bg-white shadow border m-4 p-4"
      aria-label={`LexicalDocumentEditor document ${docId}${readOnly ? ' (read-only)' : ''}`}
    >
      <div className="text-sm text-neutral-600">
        <strong>LexicalDocumentEditor Stub</strong>
        <div className="mt-2 space-y-1">
          <div><strong>Doc ID:</strong> {docId}</div>
          <div><strong>Read Only:</strong> {String(readOnly)}</div>
          <div><strong>Zoom:</strong> {zoomMode} {Math.round(userZoom * 100)}%</div>
        </div>
        <div className="mt-4 p-2 bg-gray-50 rounded text-xs">
          <strong>Status:</strong> Stub implementation - Phase 2 pending
        </div>
      </div>
    </div>
  );
});

export default LexicalDocumentEditor;






